import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import { calculateInvestorFees, formatCurrency } from "../utils/investorFees";
import { notifyUser } from "../utils/notifyUser";

export default function CardForm({
    offeringId,
    amount,
    campaignId,
    acceptanceToken,
    user,
    onSuccess,
    northCapitalAccountId,
}) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [error, setError] = useState("");
    const [searchParams] = useSearchParams();
    const [linkRedirectUrl, setLinkRedirectUrl] = useState(null);
    const [cardLinkingComplete, setCardLinkingComplete] = useState(false);

    // Use refs for tradeId and campaignId to avoid closure issues
    const tradeIdRef = useRef(null);
    const campaignIdRef = useRef(campaignId || searchParams.get("campaignId"));
    const completionAttemptedRef = useRef(false);

    // Use campaignId from props or URL
    const campaignIdParam = campaignIdRef.current;
    const feeBreakdown = calculateInvestorFees(amount, "credit_card");

    // Separate function to call complete-fundmove
    const callCompleteFundMove = async () => {
        try {
            setLoading(true);
            setLoadingMessage("Completing your investment...");
            const payload = {
                tradeId: tradeIdRef.current,
                campaignId: parseInt(campaignIdRef.current),
                acceptanceToken,
            };

            const completeResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/invest/us/card/complete-fundmove`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify(payload),
                },
            );

            const completeResult = await completeResponse.json();

            if(completeResult.success) {
                notifyUser(null, "success", "Credit card investment completed successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                navigate("/payment-success", {
                    state: {
                        amount: amount,
                        totalCharge: completeResult.investment.totalCharge,
                        fees: completeResult.investment.fees,
                        paymentMethod: "Credit Card",
                        transactionId: completeResult.investment.transactionId,
                    },
                });
                onSuccess?.(completeResult);
            } else {
                notifyUser(null, "error", "Failed to complete investment: " + completeResult.message, "toast", null, null, null, null, null, false, false, "top-center");
                setError(completeResult.message);
            }
        } catch(err) {
            console.error("Complete fund move error:", err.message);
            notifyUser(null, "error", "Failed to complete investment: " + err.message, "toast", null, null, null, null, null, false, false, "top-center");
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMessage("");
        }
    };

    // Check if user already has a linked credit card
    const [hasLinkedCard, setHasLinkedCard] = useState(false);
    const [linkedCardInfo, setLinkedCardInfo] = useState(null);
    const [checkingLinkedCard, setCheckingLinkedCard] = useState(true);
    const [paymentOption, setPaymentOption] = useState("new");
    const [updatingCard, setUpdatingCard] = useState(false);
    const creditCardCheckAttempted = useRef(false);
    const creditCardCheckInProgress = useRef(false);

    // Update payment option when linked card status changes
    useEffect(() => {
        if(hasLinkedCard) {
            setPaymentOption("existing");
        } else {
            setPaymentOption("new");
        }
    }, [hasLinkedCard]);

    // Check for existing linked credit card on component mount
    useEffect(() => {
        if(
            !northCapitalAccountId ||
            creditCardCheckAttempted.current ||
            creditCardCheckInProgress.current
        ) {
            return;
        }

        const checkLinkedCard = async () => {
            if(creditCardCheckInProgress.current) return;

            creditCardCheckInProgress.current = true;
            setCheckingLinkedCard(true);

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/trading/get-linked-credit-card`,
                    {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            accountId: northCapitalAccountId,
                        }),
                    },
                );

                if(!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                console.log("Credit card API response:", result);

                // More flexible validation for credit card data
                if(result?.data?.creditcardDetails) {
                    const cardDetails = result.data.creditcardDetails;
                    console.log("Card details:", cardDetails);

                    // Check for various possible card number fields
                    const hasCardNumber =
                        cardDetails.cardNumber ||
                        cardDetails.creditCardNumber ||
                        cardDetails.maskedCardNumber ||
                        cardDetails.last4 ||
                        cardDetails.cardNumberMask;

                    if(hasCardNumber && hasCardNumber.trim() !== "") {
                        console.log("Found linked credit card:", cardDetails);
                        setHasLinkedCard(true);
                        setLinkedCardInfo(cardDetails);
                    } else {
                        console.log("No valid card number found");
                        setHasLinkedCard(false);
                        setLinkedCardInfo(null);
                    }
                } else {
                    console.log("No creditcardDetails in response");
                    setHasLinkedCard(false);
                    setLinkedCardInfo(null);
                }
            } catch(error) {
                console.error("Error checking linked credit card:", error);
                setHasLinkedCard(false);
                setLinkedCardInfo(null);
            } finally {
                setCheckingLinkedCard(false);
                creditCardCheckInProgress.current = false;
                creditCardCheckAttempted.current = true;
            }
        };

        checkLinkedCard();
    }, [northCapitalAccountId]);

    // Update credit card function
    async function handleUpdateCreditCard(e) {
        e.preventDefault();

        if(!user.address || !user.city || !user.state || !user.zipCode) {
            notifyUser(null, "error", "Incomplete billing address. Please update your profile to proceed.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if(!northCapitalAccountId) {
            notifyUser(null, "error", "Account not found. Please contact support.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setUpdatingCard(true);
        setLoadingMessage("Preparing secure credit card update...");
        setError("");

        try {
            // First create a trade to get tradeId for updateLinkedCreditCard
            const tradeRes = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/create-trade`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        offeringId,
                        accountId: northCapitalAccountId,
                        transactionType: "CREDITCARD",
                        amount: parseFloat(amount),
                        campaignId: parseInt(campaignIdParam),
                        acceptanceToken,
                    }),
                },
            );

            if(!tradeRes.ok) {
                const errorText = await tradeRes.text();
                throw new Error(
                    `Failed to create trade: ${errorText.substring(0, 100)}`,
                );
            }

            const tradeResult = await tradeRes.json();

            // Check for success based on data property and success message
            if(
                !tradeResult.data ||
                (tradeResult.message &&
                    !tradeResult.message.toLowerCase().includes("successfully"))
            ) {
                throw new Error(
                    tradeResult.message || "Failed to create trade",
                );
            }

            // Store tradeId for later use
            const tradeId = tradeResult.data.tradeId;
            tradeIdRef.current = tradeId;

            // Try updateLinkedCreditCard first, fallback to linkCreditCard if it fails
            let updateRes;
            let updateResult;

            try {
                // First try the update-linked-credit-card endpoint
                updateRes = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/trading/update-linked-credit-card`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                            accountId: northCapitalAccountId,
                            tradeId: tradeId,
                        }),
                    },
                );

                if(!updateRes.ok) {
                    throw new Error(
                        `Update endpoint failed with status ${updateRes.status}`,
                    );
                }

                updateResult = await updateRes.json();

                // Check for success based on data property and success message
                if(
                    !updateResult.data ||
                    (updateResult.message &&
                        !updateResult.message
                            .toLowerCase()
                            .includes("successfully"))
                ) {
                    throw new Error(
                        updateResult.message ||
                            "Update endpoint returned failure",
                    );
                }
            } catch{
                // Fallback to link-credit-card endpoint
                updateRes = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/trading/link-credit-card`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({
                            accountId: northCapitalAccountId,
                            tradeId: tradeId,
                        }),
                    },
                );

                if(!updateRes.ok) {
                    const errorText = await updateRes.text();
                    throw new Error(
                        `Failed to update credit card: ${errorText.substring(0, 100)}`,
                    );
                }

                updateResult = await updateRes.json();

                if(!updateResult.success) {
                    throw new Error(
                        updateResult.message || "Failed to update credit card",
                    );
                }
            }

            // Show the redirect URL for credit card update
            if(updateResult.data.accountDetails) {
                setLinkRedirectUrl(updateResult.data.accountDetails);
                return;
            }

            throw new Error("No redirect URL received for credit card update");
        } catch(err) {
            console.error("Update credit card error:", err.message);
            setError(err.message);
            notifyUser(null, "error", "Failed to set up new credit card: " + err.message, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setUpdatingCard(false);
            setLoadingMessage("");
        }
    }

    // Handle successful card update - refresh card info
    const handleCardUpdateSuccess = async () => {
        try {
            // Refresh the linked card info
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/get-linked-credit-card`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        accountId: northCapitalAccountId,
                    }),
                },
            );

            const result = await response.json();

            // creditcardDetails is an object, not an array
            if(
                result.data &&
                result.data.creditcardDetails &&
                result.data.creditcardDetails.accountId
            ) {
                const cardInfo = result.data.creditcardDetails;
                setLinkedCardInfo(cardInfo);
                setHasLinkedCard(true);
                setPaymentOption("existing");
                setLinkRedirectUrl(null);
                notifyUser(null, "success", "Credit card updated successfully! You can now proceed with investment.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                throw new Error("Failed to retrieve updated card information");
            }
        } catch(error) {
            console.error("Error refreshing card info:", error);
            notifyUser(null, "error", "Card update may have succeeded, but failed to refresh. Please refresh the page.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Modified postMessage handler to include card update success
    useEffect(() => {
        const handlePostMessage = async (event) => {
            // Only listen for messages from trusted origins
            if(
                !event.origin.includes("localhost") &&
                !event.origin.includes("staging") &&
                !event.origin.includes("norcap")
            ) {
                return;
            }

            const dataStr = JSON.stringify(event.data).toLowerCase();
            const isExplicitSuccess =
                event.data?.success === true ||
                event.data?.status === "success" ||
                (dataStr.includes("card") && dataStr.includes("success")) ||
                (dataStr.includes("card") && dataStr.includes("added")) ||
                (dataStr.includes("card") && dataStr.includes("updated")) ||
                event.data?.message?.includes("successfully");

            if(isExplicitSuccess && !completionAttemptedRef.current) {
                completionAttemptedRef.current = true;

                // Check if this was a card update or investment completion
                if(updatingCard || paymentOption === "new") {
                    // This was a card update
                    await handleCardUpdateSuccess();
                } else if(tradeIdRef.current && campaignIdRef.current) {
                    // This was investment completion
                    setCardLinkingComplete(true);
                    await new Promise((resolve) => setTimeout(resolve, 500));
                    await callCompleteFundMove();
                }
            }
        };

        window.addEventListener("message", handlePostMessage);
        return () => {
            window.removeEventListener("message", handlePostMessage);
        };
    }, [navigate, amount, onSuccess, updatingCard, paymentOption]);
    async function handleDirectInvestment(e) {
        e.preventDefault();

        if(!user.address || !user.city || !user.state || !user.zipCode) {
            notifyUser(null, "error", "Incomplete billing address. Please update your profile to proceed.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setLoading(true);
        setLoadingMessage("Processing your credit card investment...");
        setError("");

        try {
            // Use the existing credit card endpoint with a flag for existing cards
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/invest/us/card`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        offeringId,
                        amount,
                        campaignId: parseInt(campaignIdParam),
                        acceptanceToken,
                        useExistingCard: true,
                        accountId: northCapitalAccountId,
                    }),
                },
            );

            if(!res.ok) {
                const errorText = await res.text();
                throw new Error(
                    `HTTP ${res.status}: ${errorText.substring(0, 100)}`,
                );
            }

            const result = await res.json();

            // Handle the response like the existing flow
            if(
                result.status === "card_already_linked" ||
                (result.success && !result.redirectUrl)
            ) {
                notifyUser(null, "success", "Credit card investment completed successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                navigate("/payment-success", {
                    state: {
                        amount: amount,
                        totalCharge: result.data?.totalCharge,
                        fees: result.data?.fees,
                        paymentMethod: "Credit Card",
                        transactionId: result.transactionId,
                    },
                });
                onSuccess?.(result);
                return;
            }

            // If for some reason we still get a redirect URL, handle it
            if(
                result.redirectUrl &&
                result.nextStep === "complete_card_linking"
            ) {
                tradeIdRef.current = result.tradeId;
                campaignIdRef.current = campaignIdParam;
                setLinkRedirectUrl(result.redirectUrl);
                return;
            }

            if(!result.success && !result.Success) {
                throw new Error(
                    result.message ||
                        "Failed to process credit card investment",
                );
            }

            // Fallback success
            notifyUser(null, "success", "Credit card investment completed successfully!", "toast", null, null, null, null, null, false, false, "top-center");
            navigate("/payment-success", {
                state: {
                    amount: amount,
                    totalCharge: result.data?.totalCharge,
                    fees: result.data?.fees,
                    paymentMethod: "Credit Card",
                    transactionId: result.transactionId,
                },
            });
            onSuccess?.(result);
        } catch(err) {
            console.error("Direct investment error:", err.message);
            setError(err.message);
            notifyUser(null, "error", "Failed to process investment: " + err.message, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
            setLoadingMessage("");
        }
    }

    // New simplified function - skip Stripe collection, go directly to North Capital
    async function handleDirectCardLinking(e) {
        e.preventDefault();

        if(!user.address || !user.city || !user.state || !user.zipCode) {
            notifyUser(null, "error", "Incomplete billing address. Please update your profile to proceed.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setLoading(true);
        setLoadingMessage("Preparing secure credit card linking...");
        setError("");

        try {
            // Use the existing credit card endpoint instead of non-existent link-card endpoint
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/invest/us/card`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        offeringId,
                        amount,
                        campaignId: parseInt(campaignIdParam),
                        acceptanceToken,
                        skipStripe: true,
                        newCardLinking: true,
                    }),
                },
            );

            if(!res.ok) {
                const errorText = await res.text();
                throw new Error(
                    `HTTP ${res.status}: ${errorText.substring(0, 100)}`,
                );
            }

            const result = await res.json();

            if(!result.success) {
                throw new Error(
                    result.message || "Failed to initiate card linking",
                );
            }

            // Store tradeId for later use
            tradeIdRef.current = result.tradeId;
            campaignIdRef.current = campaignIdParam;

            // Show North Capital iframe for card entry (only once)
            setLinkRedirectUrl(result.redirectUrl);
        } catch(err) {
            console.error("Direct card linking error:", err.message);
            setError(err.message);
            notifyUser(null, "error", "Failed to initiate card linking: " + err.message, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
            setLoadingMessage("");
        }
    }

    return (
        <>
            {(loading || updatingCard) && loadingMessage && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-55 p-4"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                >
                    <div className="flex min-w-[260px] max-w-sm flex-col items-center rounded-xl bg-white px-8 py-7 text-center shadow-2xl">
                        <div
                            className="mb-4 h-11 w-11 animate-spin rounded-full border-4 border-gray-200 border-t-navy"
                            aria-hidden="true"
                        />
                        <p className="font-poppins text-sm font-semibold text-[#23304a]">
                            {loadingMessage}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                            Please wait and keep this window open.
                        </p>
                    </div>
                </div>
            )}

            {/* Display iframe for card linking if redirect URL is available */}
            {linkRedirectUrl && !cardLinkingComplete && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl h-[95vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="flex justify-between items-center px-3 py-2 border-b border-gray-200 flex-shrink-0 bg-gray-50">
                            <div className="flex-1">
                                <h2 className="text-xl font-semibold text-navy">
                                    Link Your Credit Card
                                </h2>
                                <p className="text-lg text-gray-600">
                                    Complete this step to finalize your
                                    investment
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setLinkRedirectUrl(null)}
                                className="text-gray-400 hover:text-gray-600 text-lg font-light ml-2 flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Iframe Container */}
                        <div className="flex-1 min-h-0 bg-white">
                            <iframe
                                id="cardLinkingFrame"
                                src={linkRedirectUrl}
                                className="w-full h-full border-0"
                                title="Credit Card Linking"
                                sandbox="allow-same-origin allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox"
                            />
                        </div>

                        {/* Footer with Close and Complete buttons */}
                        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 flex-shrink-0">
                            <p className="text-sm text-gray-600 flex-1">
                                After linking your card above, click
                                &quot;Complete Investment&quot; to proceed.
                            </p>
                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => setLinkRedirectUrl(null)}
                                    className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-xs font-medium"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={() => callCompleteFundMove()}
                                    disabled={loading}
                                    className="px-3 py-1.5 bg-navy text-white rounded hover:bg-[#003555] disabled:opacity-50 disabled:cursor-not-allowed text-md font-medium"
                                >
                                    {loading
                                        ? "Processing..."
                                        : "Complete Investment"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Show loading state while checking for linked cards */}
            {checkingLinkedCard && (
                <div className="space-y-4">
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-pulse text-navy text-sm flex items-center space-x-2">
                            <svg
                                className="animate-spin w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span>Checking payment methods...</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Main UI - Show based on current state */}
            {!checkingLinkedCard && !linkRedirectUrl && (
                <div className="space-y-4">
                    {/* Payment Options - only show when user has a linked credit card */}
                    {hasLinkedCard && (
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-[#23304a] uppercase tracking-wide mb-2">
                                Payment Method
                            </h3>

                            {/* Option 1: Use existing linked card */}
                            <label
                                className={`flex items-center gap-3 p-3.5 border rounded-lg cursor-pointer transition-colors ${
                                    paymentOption === "existing"
                                        ? "border-navy bg-[#f0f6fa]"
                                        : "border-gray-200 bg-white hover:border-navy"
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="paymentOption"
                                    value="existing"
                                    checked={paymentOption === "existing"}
                                    onChange={(e) =>
                                        setPaymentOption(e.target.value)
                                    }
                                    className="text-navy focus:ring-navy"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <i className="fas fa-credit-card text-navy text-sm"></i>
                                        <span className="font-semibold text-[#23304a] text-sm">Linked Credit Card</span>
                                    </div>
                                    {linkedCardInfo ? (
                                        <div className="text-xs text-navy mt-0.5">
                                            {linkedCardInfo.cardType || "Card"}{" "}
                                            ending in{" "}
                                            {linkedCardInfo.creditCardNumber ||
                                                linkedCardInfo.cardNumber ||
                                                linkedCardInfo.last4 ||
                                                linkedCardInfo.maskedCardNumber ||
                                                linkedCardInfo.cardNumberMask ||
                                                "****"}
                                        </div>
                                    ) : (
                                        <div className="text-xs text-navy mt-0.5">Ready to use</div>
                                    )}
                                </div>
                            </label>

                            {/* Option 2: Update to new card - only when linked card exists */}
                            <label
                                className={`flex items-center gap-3 p-3.5 border rounded-lg cursor-pointer transition-colors ${
                                    paymentOption === "new"
                                        ? "border-navy bg-[#f0f6fa]"
                                        : "border-gray-200 bg-white hover:border-navy"
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="paymentOption"
                                    value="new"
                                    checked={paymentOption === "new"}
                                    onChange={(e) =>
                                        setPaymentOption(e.target.value)
                                    }
                                    className="text-navy focus:ring-navy"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <i className="fa-solid fa-rotate text-navy text-sm"></i>
                                        <span className="font-semibold text-[#23304a] text-sm">Use a New Card</span>
                                    </div>
                                    <div className="text-xs text-amber-600 mt-0.5">
                                        Replaces your current linked card
                                    </div>
                                </div>
                            </label>
                        </div>
                    )}

                    {/* For first-time users without linked card, show direct add card message */}
                    {!hasLinkedCard && (
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-[#23304a] uppercase tracking-wide mb-2">
                                Payment Method
                            </h3>
                            <div className="p-3.5 border border-navy rounded-lg bg-[#f0f6fa] flex items-center gap-3">
                                <i className="fas fa-credit-card text-navy"></i>
                                <div>
                                    <div className="font-semibold text-[#23304a] text-sm">Credit Card</div>
                                    <p className="text-xs text-[#6b7a8f] mt-0.5">
                                        Your card will be securely linked for this investment
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Investment Summary */}
                    <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                            <span className="text-[#6b7a8f]">Investment Amount</span>
                            <span className="font-semibold text-[#23304a]">
                                {formatCurrency(amount)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[#6b7a8f]">Fees</span>
                            <span className="font-semibold text-[#23304a]">
                                {formatCurrency(feeBreakdown?.totalFees || 0)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center border-t border-gray-100 pt-2">
                            <span className="font-semibold text-[#23304a]">Total Charge</span>
                            <span className="font-bold text-navy">
                                {formatCurrency(feeBreakdown?.totalCharge || amount)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[#6b7a8f]">Payment Method</span>
                            <span className="text-[#23304a]">
                                {!hasLinkedCard
                                    ? "Credit Card"
                                    : paymentOption === "existing"
                                      ? "Linked Credit Card"
                                      : "New Credit Card"}
                            </span>
                        </div>
                    </div>

                    {error && <p className="text-red-600 text-sm">{error}</p>}

                    {/* Action Button */}
                    {hasLinkedCard && paymentOption === "existing" ? (
                        <button
                            type="button"
                            onClick={handleDirectInvestment}
                            disabled={loading || updatingCard}
                            className="w-full py-3 px-4 bg-navy hover:bg-navy-2 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                "Processing..."
                            ) : (
                                <>
                                    <i className="fas fa-credit-card mr-2"></i>
                                    Pay {formatCurrency(feeBreakdown?.totalCharge || amount)} with Linked Card
                                </>
                            )}
                        </button>
                    ) : hasLinkedCard && paymentOption === "new" ? (
                        <button
                            type="button"
                            onClick={handleUpdateCreditCard}
                            disabled={loading || updatingCard}
                            className="w-full py-3 px-4 bg-navy hover:bg-navy-2 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {updatingCard ? (
                                "Setting up new card..."
                            ) : (
                                <>
                                    <i className="fa-solid fa-rotate mr-2"></i>
                                    Update Card & Pay {formatCurrency(feeBreakdown?.totalCharge || amount)}
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleDirectCardLinking}
                            disabled={loading || updatingCard}
                            className="w-full py-3 px-4 bg-navy hover:bg-navy-2 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                "Setting up card..."
                            ) : (
                                <>
                                    <i className="fas fa-credit-card mr-2"></i>
                                    Add Card & Pay {formatCurrency(feeBreakdown?.totalCharge || amount)}
                                </>
                            )}
                        </button>
                    )}

                    <p className="text-xs text-gray-500 text-center mt-2">
                        Your payment information is secured and encrypted. We do
                        not store your credit card details.
                    </p>
                </div>
            )}
        </>
    );
}

CardForm.propTypes = {
    offeringId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    acceptanceToken: PropTypes.string.isRequired,
    user: PropTypes.shape({
        address: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zipCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    onSuccess: PropTypes.func.isRequired,
    northCapitalAccountId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
};
